/**
 * QuestViewModal — Premium branded quest detail modal
 * Amber/brown theme with quest prompt banner and creation steps
 */

import React from 'react';
import { Button } from '@mui/material';
import {
  Close as CloseIcon,
  Mic as MicIcon,
  TextFields as TextIcon,
  PhotoCamera as PhotoIcon,
  Brush as StyleIcon,
  AspectRatio as LengthIcon,
  Person as PersonIcon,
  Palette as IllustrationIcon,
  Image as ImageIcon,
  MoodOutlined as MoodIcon,
  EmojiEvents as XPIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import './QuestViewModal.css';

interface CreationSteps {
  inputMethod: { step: number; label: string; value: string | null };
  recording: { step: number; label: string; durationSeconds: number | null; rawWordCount: number | null; photosUploaded: number };
  writingStyle: { step: number; label: string; value: string | null };
  storyLength: { step: number; label: string; value: string | null };
  narrativePerspective: { step: number; label: string; value: string | null };
  illustrationStyle: { step: number; label: string; value: string | null };
  aiImages: { step: number; label: string; requested: number; generated: number };
  mood: { step: number; label: string; value: string | null };
}

interface QuestDetail {
  id: string;
  title?: string;
  story?: string;
  originalText?: string;
  status?: string;
  wordCount?: number;
  created?: string;
  updated?: string;
  writingStyle?: string;
  illustrationStyle?: string;
  mood?: string;
  source?: string;
  questPromptId?: number;
  questPrompt?: string;
  user?: { id: string; email: string; subscriptionPlan?: string };
  creationSteps?: CreationSteps | null;
  xpEarned?: number;
  images?: any[] | null;
}

interface QuestViewModalProps {
  open: boolean;
  onClose: () => void;
  quest: QuestDetail | null;
}

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
};

const QuestViewModal: React.FC<QuestViewModalProps> = ({ open, onClose, quest }) => {
  if (!open || !quest) return null;

  const steps = quest.creationSteps;

  const stepItems = steps ? [
    {
      label: steps.inputMethod.label,
      value: steps.inputMethod.value,
      filled: !!steps.inputMethod.value,
    },
    {
      label: steps.recording.label,
      value: steps.recording.durationSeconds
        ? formatDuration(steps.recording.durationSeconds)
        : steps.recording.rawWordCount
          ? `${steps.recording.rawWordCount} words`
          : null,
      filled: !!(steps.recording.durationSeconds || steps.recording.rawWordCount),
    },
    {
      label: steps.writingStyle.label,
      value: steps.writingStyle.value,
      filled: !!steps.writingStyle.value,
    },
    {
      label: steps.storyLength.label,
      value: steps.storyLength.value,
      filled: !!steps.storyLength.value,
    },
    {
      label: steps.narrativePerspective.label,
      value: steps.narrativePerspective.value?.replace('_', ' '),
      filled: !!steps.narrativePerspective.value,
    },
    {
      label: steps.illustrationStyle.label,
      value: steps.illustrationStyle.value,
      filled: !!steps.illustrationStyle.value,
    },
    {
      label: steps.aiImages.label,
      value: steps.aiImages.requested > 0 ? `${steps.aiImages.requested} requested, ${steps.aiImages.generated} generated` : null,
      filled: steps.aiImages.requested > 0,
      teal: true,
    },
    {
      label: steps.mood.label,
      value: steps.mood.value,
      filled: !!steps.mood.value,
    },
  ] : [];

  const hasOriginalText = quest.originalText && quest.originalText.trim().length > 0;
  const hasStory = quest.story && quest.story.trim().length > 0;
  const showComparison = hasOriginalText && hasStory && quest.originalText !== quest.story;

  return (
    <div className="quest-view-modal-overlay" onClick={onClose}>
      <div className="quest-view-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="quest-modal-header">
          <button className="quest-modal-close" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
          <h2 className="quest-modal-title">{quest.title || 'Untitled Quest'}</h2>
          <div className="quest-modal-meta-bar">
            <span className={`quest-modal-status quest-modal-status--${quest.status || 'draft'}`}>
              {quest.status || 'draft'}
            </span>
            {quest.user?.email && (
              <span className="quest-modal-meta-item">
                <PersonIcon sx={{ fontSize: 14 }} />
                {quest.user.email}
              </span>
            )}
            <span className="quest-modal-meta-item">
              {(quest.wordCount || 0).toLocaleString()} words
            </span>
            {quest.xpEarned != null && quest.xpEarned > 0 && (
              <span className="quest-xp-badge">
                <XPIcon sx={{ fontSize: 14 }} />
                {quest.xpEarned} XP
              </span>
            )}
            {quest.created && (
              <span className="quest-modal-meta-item">
                {format(new Date(quest.created), 'MMM d, yyyy')}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="quest-modal-body">
          {/* Quest Prompt Banner */}
          {quest.questPrompt && (
            <div className="quest-prompt-banner">
              <div className="quest-prompt-banner-label">Legacy Quest Prompt</div>
              <div className="quest-prompt-banner-text">"{quest.questPrompt}"</div>
            </div>
          )}

          {/* Creation Steps */}
          {stepItems.length > 0 && (
            <>
              <h3 className="quest-steps-title">Creation Process</h3>
              <div className="quest-steps-timeline">
                {stepItems.map((item, i) => (
                  <div className="quest-step" key={i}>
                    <div className={`quest-step-dot ${item.filled ? (item.teal ? 'quest-step-dot--teal' : 'quest-step-dot--filled') : ''}`}>
                      {i + 1}
                    </div>
                    <span className="quest-step-label">{item.label}</span>
                    <span className={`quest-step-value ${!item.value ? 'quest-step-value--empty' : ''}`}>
                      {item.value || 'Not set'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Content — comparison or single */}
          <div className="quest-content-section">
            <h3 className="quest-content-label">Quest Content</h3>
            <div className={`quest-content-comparison ${!showComparison ? 'quest-content-comparison--single' : ''}`}>
              {showComparison && (
                <div className="quest-content-box">
                  <div className="quest-content-box-label">Original Text</div>
                  <div className="quest-content-box-text">{quest.originalText}</div>
                </div>
              )}
              <div className="quest-content-box">
                <div className="quest-content-box-label">{showComparison ? 'AI-Enhanced Story' : 'Story'}</div>
                <div className="quest-content-box-text">
                  {quest.story || quest.originalText || 'No content available'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="quest-modal-footer">
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: '8px' }}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuestViewModal;
